import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface AuthConfig {
  authMode?: string;
  requireMfa?: boolean | null;
  enablePasskeys?: boolean | null;
  allowLocalLogin?: boolean;
}

interface SsoProvider {
  id: number;
  providerType: string;
  displayName: string | null;
  domain: string | null;
  isEnabled: boolean;
}

interface SecurityData {
  authConfig: AuthConfig | null;
  ssoProviders: SsoProvider[];
  ssoEnabled: boolean;
}

export default function SecurityPanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [data, setData] = useState<SecurityData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/security");
      if (resp.ok) {
        setData(await resp.json());
      }
    } catch {
      // Graceful — show defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hasSso = subscription?.enableEnterpriseSso ?? false;
  const hasMfa = subscription?.enableAdvancedMfa ?? false;
  const hasPasskeys = subscription?.enablePasskeys ?? false;
  const ssoProviders = Array.isArray(data?.ssoProviders) ? data!.ssoProviders : [];
  const authMode = data?.authConfig?.authMode ?? "V10";
  // SSO is effectively active if auth mode uses Entra, even if subscription flag isn't set
  const ssoEffectivelyActive = hasSso || authMode === "EntraId" || authMode === "Dual";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cornflower-blue-500" />
        <span className="ml-3 text-slate-gray-500 text-sm">Loading security settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-gray-800">Security & Authentication</h2>
        <p className="text-sm text-slate-gray-500 mt-1">
          View your account's authentication settings, SSO configuration, and security features.
        </p>
      </div>

      {/* Current Auth Status */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-3">Authentication Status</h3>
        <div className="grid grid-cols-2 gap-4">
          <StatusCard
            label="Login Method"
            value={
              authMode === "EntraId"
                ? hasSso ? "SSO Only" : "Entra ID"
                : authMode === "Dual"
                  ? hasSso ? "SSO + Password" : "Entra + Password"
                  : "Password"
            }
            active={authMode !== "V10"}
          />
          <StatusCard
            label="Multi-Factor Authentication"
            value={data?.authConfig?.requireMfa ? "Required" : hasMfa ? "Available" : "Not included"}
            active={!!data?.authConfig?.requireMfa}
          />
        </div>
      </div>

      {/* SSO Section */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-1">Single Sign-On (SSO)</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Allow your team to log in with your company's identity provider — Microsoft Azure AD, Okta, OneLogin, or any OIDC/SAML provider.
        </p>

        {ssoEffectivelyActive ? (
          <>
            {ssoProviders.length > 0 ? (
              <div className="space-y-2 mb-4">
                {ssoProviders.map((p) => (
                  <div key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border ${p.isEnabled ? "border-emerald-200 bg-emerald-50/50" : "border-slate-gray-200 bg-slate-gray-50"}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${p.isEnabled ? "bg-emerald-500" : "bg-slate-gray-400"}`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-gray-800">{p.displayName ?? p.providerType}</div>
                      <div className="text-xs text-slate-gray-500">
                        {p.providerType} {p.domain ? `— ${p.domain}` : ""}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.isEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-gray-100 text-slate-gray-500"}`}>
                      {p.isEnabled ? "Active" : "Inactive"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-emerald-800 font-medium">
                  {authMode === "EntraId" ? "Entra ID authentication is active" : "Entra ID + password login is active"}
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  Your site uses Entra External ID for authentication.
                  {hasSso && " Contact your Velaro administrator to configure additional identity providers."}
                </p>
              </div>
            )}

            {hasSso && <SsoSetupGuide />}
          </>
        ) : (
          <div className="bg-slate-gray-50 rounded-lg p-4 text-sm text-slate-gray-600">
            <p className="font-medium text-slate-gray-700">SSO is not included in your current plan</p>
            <p className="mt-1">Upgrade to an Enterprise plan to enable Single Sign-On for your team. Contact sales@velaro.com for pricing.</p>
          </div>
        )}
      </div>

      {/* MFA Section */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-1">Multi-Factor Authentication (MFA)</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Add an extra layer of security by requiring a second form of verification when logging in.
        </p>

        {hasMfa ? (
          <div className="space-y-3">
            <div className={`${data?.authConfig?.requireMfa ? "bg-emerald-50 border-emerald-200" : "bg-cornflower-blue-50 border-cornflower-blue-200"} border rounded-lg p-4 text-sm`}>
              <p className={`font-medium ${data?.authConfig?.requireMfa ? "text-emerald-800" : "text-cornflower-blue-800"}`}>
                {data?.authConfig?.requireMfa
                  ? "MFA is required for all users on this site"
                  : "MFA is available but not required"}
              </p>
              <p className={`mt-1 ${data?.authConfig?.requireMfa ? "text-emerald-600" : "text-cornflower-blue-600"}`}>
                {data?.authConfig?.requireMfa
                  ? "All users must set up a verification method. Users who haven't configured MFA will be prompted at login."
                  : "MFA can be enforced by your Velaro administrator. Users can optionally set it up for extra security."}
              </p>
            </div>
            <div className="text-sm text-slate-gray-600">
              <p className="font-medium mb-2">Supported MFA methods:</p>
              <ul className="space-y-1.5 ml-4">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cornflower-blue-500" />
                  SMS verification code
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cornflower-blue-500" />
                  Email one-time passcode (OTP)
                </li>
              </ul>
            </div>
            <div className="bg-slate-gray-50 border border-slate-gray-200 rounded-lg p-3 text-xs text-slate-gray-600">
              <strong>User setup:</strong> Users can manage their MFA methods from{" "}
              <span className="font-semibold text-cornflower-blue-600">Profile &gt; Security</span>.
              Phone numbers for SMS verification can be added or removed there.
            </div>
          </div>
        ) : (
          <div className="bg-slate-gray-50 rounded-lg p-4 text-sm text-slate-gray-600">
            <p className="font-medium text-slate-gray-700">MFA is not included in your current plan</p>
            <p className="mt-1">Upgrade to enable multi-factor authentication. Contact sales@velaro.com for pricing.</p>
          </div>
        )}
      </div>

      {/* Passkeys Section */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="font-semibold text-slate-gray-800 mb-1">Passkeys / FIDO2</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          Use biometric authentication (fingerprint, face recognition) or hardware security keys for passwordless login.
        </p>

        {hasPasskeys ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
            <p className="font-medium text-emerald-800">Passkeys are available for your account</p>
            <p className="text-emerald-600 mt-1">Once enabled by your administrator, you can register a passkey from your profile settings to log in without a password.</p>
          </div>
        ) : (
          <div className="bg-slate-gray-50 rounded-lg p-4 text-sm text-slate-gray-600">
            <p className="font-medium text-slate-gray-700">Passkeys are not included in your current plan</p>
            <p className="mt-1">Upgrade to enable passwordless authentication. Contact sales@velaro.com for pricing.</p>
          </div>
        )}
      </div>

      {/* Help / Contact */}
      <SupportRequestForm />
    </div>
  );
}

/* ── SSO Setup Guide (what IT needs to provide) ── */

function SsoSetupGuide() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-gray-200 rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-gray-50 transition"
        aria-expanded={expanded}
      >
        <div>
          <span className="text-sm font-medium text-slate-gray-800">SSO Setup Guide — What Your IT Team Needs</span>
          <span className="block text-xs text-slate-gray-500 mt-0.5">Step-by-step instructions for each identity provider</span>
        </div>
        <svg className={`w-5 h-5 text-slate-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-slate-gray-200 p-4 space-y-4">
          <ProviderGuide
            name="Microsoft Azure AD / Entra ID"
            recommended
            items={[
              "Provide your Azure AD Tenant ID (found in Azure Portal > Microsoft Entra ID > Overview)",
              "Tell us which email domain(s) to route (e.g., @yourcompany.com)",
              "That's it — Microsoft's OpenID Connect handles everything else automatically",
            ]}
          />

          <ProviderGuide
            name="Okta"
            items={[
              "Create an OIDC Web Application in Okta Admin > Applications > Create App Integration",
              "Set redirect URI to: https://login.velaro.com/oauth2/callback",
              "Send us: Client ID, Client Secret, and your Okta domain (e.g., yourcompany.okta.com)",
              "Tell us the email domain to route",
            ]}
          />

          <ProviderGuide
            name="OneLogin"
            items={[
              "Create an OIDC connector: Apps > Add App > search 'OpenID Connect'",
              "Set redirect URI to: https://login.velaro.com/oauth2/callback",
              "Send us: Client ID, Client Secret, and your OneLogin subdomain",
              "Tell us the email domain to route",
            ]}
          />

          <ProviderGuide
            name="Other OIDC / SAML Provider"
            items={[
              "Provide the OIDC metadata URL (ends in /.well-known/openid-configuration) or SAML metadata URL",
              "If OIDC: create an app and send us Client ID + Client Secret",
              "Set redirect URI to: https://login.velaro.com/oauth2/callback",
            ]}
          />

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            <strong>Redirect URI for all providers:</strong>{" "}
            <code className="bg-white px-1.5 py-0.5 rounded text-amber-800 select-all">https://login.velaro.com/oauth2/callback</code>
            <p className="mt-1">Your IT team must add this URL as an allowed redirect URI in the identity provider's app registration.</p>
          </div>

          <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-3 text-xs text-cornflower-blue-700">
            <strong>Break-glass access:</strong> Even with SSO enabled, designated admin accounts can still log in with email + password. This ensures you're never locked out if your identity provider has an outage.
          </div>
        </div>
      )}
    </div>
  );
}

function ProviderGuide({ name, items, recommended }: { name: string; items: string[]; recommended?: boolean }) {
  return (
    <div className="bg-slate-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-slate-gray-800">{name}</span>
        {recommended && (
          <span className="text-[10px] bg-cornflower-blue-500 text-white px-1.5 py-0.5 rounded font-bold">
            RECOMMENDED
          </span>
        )}
      </div>
      <ul className="text-sm text-slate-gray-600 space-y-1 ml-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">{i + 1}.</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusCard({ label, value, active }: { label: string; value: string; active: boolean }) {
  return (
    <div className={`rounded-lg p-3 border ${active ? "border-emerald-200 bg-emerald-50" : "border-slate-gray-200 bg-slate-gray-50"}`}>
      <div className="text-xs text-slate-gray-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${active ? "text-emerald-700" : "text-slate-gray-600"}`}>{value}</div>
    </div>
  );
}

function SupportRequestForm() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [subject, setSubject] = useState("SSO Setup Request");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [chatLoaded, setChatLoaded] = useState(false);
  const chatContainerRef = React.useRef<HTMLDivElement>(null);

  // Load the support chat widget (deploy code from site 1032 global settings)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.messaging.get("admin/support-chat");
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (!data?.enabled || !data?.deployCode || cancelled) return;

        // Inject the deploy code into the container — it contains <script> tags
        // that need to execute, so we parse and re-create script elements
        if (chatContainerRef.current) {
          const container = chatContainerRef.current;
          container.innerHTML = data.deployCode;

          // <script> tags inserted via innerHTML don't execute — re-create them
          const scripts = container.querySelectorAll("script");
          scripts.forEach((oldScript) => {
            const newScript = document.createElement("script");
            Array.from(oldScript.attributes).forEach((attr) =>
              newScript.setAttribute(attr.name, attr.value)
            );
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode?.replaceChild(newScript, oldScript);
          });

          setChatLoaded(true);
        }
      } catch {
        // Chat widget is optional — failing silently is fine
      }
    })();
    return () => { cancelled = true; };
  }, [api.messaging]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      const resp = await api.messaging.post("admin/support-request", { subject, message });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Support request sent successfully.");
        setMessage("");
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to send support request. Please try again.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to send support request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-5">
      <h3 className="font-semibold text-cornflower-blue-800 mb-2">Need help with security?</h3>
      <p className="text-sm text-cornflower-blue-700 mb-4">
        Our team can help you set up SSO, configure MFA policies, or answer questions about securing your Velaro account.
        {chatLoaded && (
          <span className="ml-1 font-medium">Use the chat widget to talk to us live, or send a message below.</span>
        )}
      </p>

      {/* Support chat widget container — deploy code injected from site 1032 settings */}
      <div ref={chatContainerRef} />

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="support-subject" className="block text-xs font-medium text-cornflower-blue-700 mb-1">
            Subject
          </label>
          <input
            id="support-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-cornflower-blue-200 rounded-lg bg-white text-slate-gray-800 focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          />
        </div>
        <div>
          <label htmlFor="support-message" className="block text-xs font-medium text-cornflower-blue-700 mb-1">
            Message
          </label>
          <textarea
            id="support-message"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe what you need help with..."
            className="w-full px-3 py-2 text-sm border border-cornflower-blue-200 rounded-lg bg-white text-slate-gray-800 resize-y focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={sending || !message.trim()}
          className="px-4 py-2 bg-cornflower-blue-500 text-white rounded-lg text-sm font-medium hover:bg-cornflower-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? "Sending..." : "Send to Support"}
        </button>
      </form>
    </div>
  );
}
